/**
 * DD-LOC-RULES-001 (F-032 Amanda Skalabrin + F-028 Jessica Padilla): on a New Product line the
 * Design Location and the Production Location are derived, not chosen by hand — both stay
 * editable in Triage / Edit Line for the rare exception.
 *
 * Design Location ← Standardization Type + marketplace (Amanda, 2026-09-29):
 *   every marketplace but Walmart: Standard = NO Design; Standard-Custom / Controlled Custom =
 *   Design (QP); Walmart (one marketplace with Sam's Club in PIM / PIQ): always Design (QP).
 *   Complex Custom is not requested at intake (F-064) and has no rule.
 *
 * Production Location ← the build (Amanda's option b; Nadia: in PIM this is the Preferred
 * Location — EVT = small format, LAN = ADA, empty = both plants):
 *   ADA (the "Braille & ADA Signs" category or an ADA material) → Lancaster, PA — checked first,
 *   so a small-format ADA piece goes to Lancaster too; the "Small Format Printed Pieces"
 *   category (the only category the Sign Builder's "small format" choice allows) → Everett, WA;
 *   anything else → Both Locations.
 */
import type { OriginSpec } from '@/types/origin'
import {
  DESIGN_LOCATION_NONE,
  DESIGN_LOCATION_QP,
  PRODUCTION_LOCATION_BOTH,
  PRODUCTION_LOCATION_EVERETT,
  PRODUCTION_LOCATION_LANCASTER
} from '@/lib/dd-queue/data/attributeSchema'
import { findSubstrateById } from '@/lib/dd-queue/substrateCatalogOptions'

/** Sign Builder category ids (mockSignBuilderCatalog SIGN_CATEGORIES). */
export const SMALL_FORMAT_CATEGORY_ID = 'small-format'
export const ADA_CATEGORY_ID = 'ada-specialty'
/** Catalog `matType` of the ADA materials (Rowmark / engraving acrylics). */
export const ADA_MAT_TYPE = 'ada'

/**
 * Walmart and Sam's Club are one marketplace ("Walmart/Sam's Club" in PIM, VSG-Walmart in
 * PIQ) — the Client field of a request in any of its spellings. One source of truth for every
 * rule keyed on it: Design (QP) for every Standardization Type (F-032) and the SignAgent SVG
 * deliverable ticked by default (F-027, DD-SVG-DEFAULT-001).
 */
const WALMART_MARKETPLACE_CUSTOMERS = new Set([
  'walmart',
  "walmart / sam's club",
  "walmart/sam's club",
  "sam's club"
])

export function isWalmartMarketplace(customer: string | null | undefined): boolean {
  return WALMART_MARKETPLACE_CUSTOMERS.has((customer ?? '').trim().toLowerCase())
}

const DESIGN_LOCATION_BY_STD_TYPE: Record<string, string> = {
  Standard: DESIGN_LOCATION_NONE,
  'Standard-Custom': DESIGN_LOCATION_QP,
  'Controlled Custom': DESIGN_LOCATION_QP
}

/** Design Location is always Design (QP) on this marketplace (Amanda, 2026-09-29). */
export function isQpDesignCustomer(customer: string | null | undefined): boolean {
  return isWalmartMarketplace(customer)
}

/**
 * Design Location for a Standardization Type on a marketplace; `null` when the type has no
 * rule (empty, Complex Custom, an unknown value) — the field is then left as it is.
 */
export function designLocationFor(
  stdType: string | null | undefined,
  customer: string | null | undefined
): string | null {
  const type = (stdType ?? '').trim()
  const byType = DESIGN_LOCATION_BY_STD_TYPE[type]
  if (!byType) return null
  return isQpDesignCustomer(customer) ? DESIGN_LOCATION_QP : byType
}

type SpecLike = Pick<OriginSpec, 'category' | 'base_substrate'> | null | undefined

export function isAdaBuild(spec: SpecLike): boolean {
  if (!spec) return false
  if (spec.category?.id === ADA_CATEGORY_ID) return true
  const sub = spec.base_substrate
  if (!sub) return false
  const matType = sub.matType ?? findSubstrateById(sub.id)?.matType
  return matType === ADA_MAT_TYPE
}

export function isSmallFormatBuild(spec: SpecLike): boolean {
  return spec?.category?.id === SMALL_FORMAT_CATEGORY_ID
}

/** Production Location of a New Product build — never empty ("Both Locations" is the default). */
export function productionLocationFor(spec: SpecLike): string {
  if (isAdaBuild(spec)) return PRODUCTION_LOCATION_LANCASTER
  if (isSmallFormatBuild(spec)) return PRODUCTION_LOCATION_EVERETT
  return PRODUCTION_LOCATION_BOTH
}
